import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import { FileText } from "lucide-react";
import { usePortalLanguage, tLang } from "@/contexts/PortalLanguageContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Brand tokens
const NAVY  = "#1A1A2E";
const EMBER = "#FF6B35";
const TEAL  = "#00BFA5";

export default function RetailersPricing() {
  const { t } = usePortalLanguage();
  const { pathname } = useLocation();
  const locale = pathname.startsWith("/fr") ? "fr" : pathname.startsWith("/es") ? "es" : "en";

  // ─── Pricing plans ────────────────────────────────────────────────────────
  // Strings reuse retailers.pricing.* keys so copy stays in sync with Retailers.tsx

  const pricingTailored = t("retailers.pricing.tailored", "Pricing tailored to your store");
  const pricingOrderForm = t("retailers.pricing.orderFormLine", "Set out in your Order Form");

  const pricingPlans = [
    {
      key:      "founding",
      name:     t("retailers.pricing.founding.name",    "Founding Retailer"),
      badge:    t("retailers.pricing.founding.badge",   "First cohort"),
      subtext:  t("retailers.pricing.founding.subtext", "A limited first cohort of retailers"),
      features: [
        t("retailers.pricing.founding.f1", "AR visualization widget for your website"),
        t("retailers.pricing.founding.f2", "Access to the Fyrris product catalogue"),
        t("retailers.pricing.founding.f3", "Basic admin portal"),
        t("retailers.pricing.founding.f4", "Email support"),
        t("retailers.pricing.founding.f5", "Rate locked for the term of your agreement"),
        t("retailers.pricing.founding.f6", "Early access to new features"),
        t("retailers.pricing.founding.f7", "Direct line to the founder"),
      ],
      cta:      t("retailers.pricing.founding.cta",     "Book a Demo"),
      ctaHref:  locale === "es" ? "/es/minoristas/demo" : locale === "fr" ? "/fr/detaillants/demo" : "/retailers/demo",
      dotColor:   EMBER,
      ctaColor:   EMBER,
      badgeClass: "bg-orange-100 text-orange-700",
    },
    {
      key:      "standard",
      name:     t("retailers.pricing.standard.name",    "Standard"),
      badge:    t("retailers.pricing.standard.badge",   "Most Popular"),
      subtext:  "",
      features: [
        t("retailers.pricing.standard.f1", "Everything in Founding"),
        t("retailers.pricing.standard.f2", "Full catalogue access & custom products"),
        t("retailers.pricing.standard.f3", "AR analytics & session data"),
        t("retailers.pricing.standard.f4", "Lead capture & contact data"),
        t("retailers.pricing.standard.f5", "Priority support"),
      ],
      cta:      t("retailers.pricing.standard.cta",     "Book a Demo"),
      ctaHref:  locale === "es" ? "/es/minoristas/demo" : locale === "fr" ? "/fr/detaillants/demo" : "/retailers/demo",
      dotColor:   TEAL,
      ctaColor:   TEAL,
      badgeClass: "bg-teal-100 text-teal-700",
    },
    {
      key:      "growth",
      name:     t("retailers.pricing.growth.name",      "Growth"),
      badge:    t("retailers.pricing.growth.badge",     "Multi-Location"),
      subtext:  t("retailers.pricing.growth.subtext",   "White-label ready · multi-location"),
      features: [
        t("retailers.pricing.growth.f1", "Everything in Standard"),
        t("retailers.pricing.growth.f2", "White-label AR experience"),
        t("retailers.pricing.growth.f3", "Advanced analytics & reporting"),
        t("retailers.pricing.growth.f4", "Multi-location management"),
        t("retailers.pricing.growth.f5", "Dedicated account manager"),
      ],
      cta:      t("retailers.pricing.growth.cta",       "Book a Demo"),
      ctaHref:  locale === "es" ? "/es/minoristas/demo" : locale === "fr" ? "/fr/detaillants/demo" : "/retailers/demo",
      dotColor:   "#9CA3AF",
      ctaColor:   "#374151",
      badgeClass: "bg-gray-100 text-gray-500",
    },
  ];

  // ─── FAQ items ────────────────────────────────────────────────────────────
  // Same questions as Retailers.tsx — reuse retailers.faq.* keys

  const faqItems = [
    {
      q: t("retailers.faq.q1", "What products can I onboard?"),
      a: t("retailers.faq.a1", "Any physical product in the HPB space — fireplaces, wood stoves, gas inserts, BBQ grills, patio furniture, outdoor kitchens, and more. If it has dimensions, we can build an AR model for it."),
    },
    {
      q: t("retailers.faq.q2", "How long does setup take?"),
      a: t("retailers.faq.a2", "Most retailers are live within 2–3 weeks. We handle all the 3D processing. You submit your product list and photos; we take it from there."),
    },
    {
      q: t("retailers.faq.q3", "Do I need technical staff?"),
      a: t("retailers.faq.a3", "No. The Fyrris portal is built for non-technical users. If you can update a spreadsheet, you can manage your full product catalogue and locations."),
    },
    {
      q: t("retailers.faq.q4", "What happens after the founding cohort is full?"),
      a: t("retailers.faq.a4", "Founding is a limited first cohort. Retailers who join later still get pricing tailored to their store, set out in their Order Form. Any rate commitment is whatever your Order Form states."),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="bg-gray-950 text-gray-100">
      <Helmet>
        <title>{tLang(locale, "pricing.meta.title", "Retailer Pricing — Fyrris")}</title>
        <meta name="description" content={tLang(locale, "pricing.meta.description")} />
        <meta name="keywords" content="HPB retailer pricing, Fyrris plan, fireplace retailer software cost, AR platform subscription, patio retailer tools, BBQ store software" />
        <meta property="og:title" content={tLang(locale, "pricing.meta.ogTitle")} />
        <meta property="og:description" content={tLang(locale, "pricing.meta.ogDescription")} />
        <meta property="og:image" content="https://www.fyrris.com/fyrris-social-banner.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={tLang(locale, "pricing.meta.ogTitle")} />
        <meta name="twitter:description" content={tLang(locale, "pricing.meta.ogDescription")} />
      </Helmet>

      {/* ── SECTION 1 · HERO ────────────────────────────────────────────── */}
      <section
        className="pt-12 pb-16 md:pt-14 md:pb-20 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <p
            className="inline-block text-xs font-semibold tracking-widest uppercase mb-5 px-3 py-1 rounded-full"
            style={{ backgroundColor: `${EMBER}22`, color: EMBER }}
          >
            {t("pricing.hero.eyebrow", "Pricing")}
          </p>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5">
            {t("pricing.hero.headline", "Simple, transparent pricing.")}
          </h1>

          <p className="text-lg md:text-xl text-gray-300 max-w-xl mx-auto">
            {t(
              "pricing.hero.subhead",
              "Built for HPB specialty retailers. No hidden fees. Cancel anytime."
            )}
          </p>
        </div>
      </section>

      {/* ── SECTION 2 · PRICING GRID ────────────────────────────────────── */}
      <section
        className="py-16 md:py-24 px-4 sm:px-6 lg:px-8"
        style={{
          backgroundColor: "#030712",
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Section heading */}
          <div className="text-center mb-14">
            <p
              className="inline-block text-xs font-semibold tracking-widest uppercase mb-4 px-3 py-1 rounded-full"
              style={{ backgroundColor: `${EMBER}22`, color: EMBER }}
            >
              {t("pricing.plans.eyebrow", "Choose your plan")}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
              {t("retailers.pricing.title", "Pricing tailored to your store")}
            </h2>
            <p className="text-gray-300 mb-6">
              {t("retailers.pricing.subtitle", "Set out in your Order Form, so there are no surprises.")}
            </p>
            <p className="text-sm text-gray-300 italic">
              {t("pricing.trustLine", "Joined by HPB retailers across North America")}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {pricingPlans.map((plan) => (
              <div
                key={plan.key}
                className={`relative rounded-2xl flex flex-col bg-white shadow-xl overflow-hidden ${
                  plan.key === "founding" ? "md:scale-[1.02] md:z-10" : ""
                }`}
                style={plan.key === "founding" ? { border: `2px solid ${EMBER}` } : { border: "1px solid #E5E7EB" }}
              >
                {/* 1. Badge pill */}
                <div className="px-6 pt-6">
                  <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${plan.badgeClass}`}>
                    {plan.badge}
                  </span>
                </div>

                {/* 2. Plan name */}
                <div className="px-6 pt-3">
                  <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                </div>

                {/* 3. Pricing line: no public prices. Tailored to the store, set out in the Order Form. */}
                <div className="px-6 pt-2">
                  <p className="text-lg font-semibold text-gray-900">{pricingTailored}</p>
                  <p className="text-sm text-gray-500">{pricingOrderForm}</p>
                </div>

                {/* 4. Subtext */}
                {plan.subtext && <p className="px-6 pt-1 text-sm text-gray-500">{plan.subtext}</p>}

                {/* 5. Divider */}
                <div className="mx-6 mt-5 border-t border-gray-100" />

                {/* 6. Feature list with dot bullets */}
                <ul className="px-6 pt-5 flex flex-col gap-3 flex-1">
                  {plan.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <span
                        className="mt-1.5 h-2 w-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: plan.dotColor }}
                      />
                      {feat}
                    </li>
                  ))}
                </ul>

                {/* 8. Divider */}
                <div className="mx-6 mt-4 border-t border-gray-100" />

                {/* 10. CTA */}
                <div className="px-6 pt-4 pb-6">
                  <Link
                    to={plan.ctaHref}
                    className="w-full inline-flex items-center justify-center rounded-full px-10 py-3 text-sm font-semibold text-white whitespace-nowrap transition hover:opacity-90"
                    style={{ backgroundColor: plan.ctaColor }}
                  >
                    {plan.cta}
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Pricing note */}
          <div className="mt-10 mx-auto max-w-md bg-gray-900 rounded-xl p-4 flex items-center gap-3">
            <FileText className="w-5 h-5 flex-shrink-0" style={{ color: TEAL }} />
            <p className="text-sm text-gray-300">
              {t(
                "retailers.pricing.orderFormNote",
                "Your pricing is tailored to your store and set out in your Order Form."
              )}
            </p>
          </div>

          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginTop: '1rem' }}>
            {t("pricing.currencyNote", "Pricing is available in CAD or USD.")}
          </p>

        </div>
      </section>

      {/* ── SECTION 3 · FAQ ─────────────────────────────────────────────── */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: NAVY }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            {t("retailers.faq.title", "Frequently asked questions")}
          </h2>
          <p className="text-center text-gray-300 mb-12">
            {t("retailers.faq.subtitle", "Still have questions? Reach out at support@fyrris.com")}
          </p>

          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, idx) => (
              <AccordionItem
                key={idx}
                value={`item-${idx}`}
                className="border-gray-800"
              >
                <AccordionTrigger className="text-white hover:text-white text-base font-semibold py-5 text-left">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-300 pb-5 leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ── SECTION 4 · BOTTOM CTA ──────────────────────────────────────── */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-950" style={{ marginTop: '5rem' }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t("pricing.cta.headline", "Ready to grow your showroom?")}
          </h2>
          <p className="text-gray-300 mb-8">
            {t(
              "pricing.cta.body",
              "Join a limited first cohort of retailers and help shape the platform."
            )}
          </p>
          <Link
            to={locale === "es" ? "/es/minoristas/demo" : locale === "fr" ? "/fr/detaillants/demo" : "/retailers/demo"}
            className="transition hover:opacity-90"
            style={{
              backgroundColor: "#FF6B35",
              color: "white",
              borderRadius: "9999px",
              padding: "0.75rem 2.5rem",
              whiteSpace: "nowrap",
              fontWeight: "500",
              width: "auto",
              display: "inline-block",
              boxShadow: "0 4px 14px rgba(255,107,53,0.3)",
            }}
          >
            {t("pricing.cta.button", "Book a Demo")}
          </Link>
          <p className="mt-4 text-xs text-gray-400">
            {t("pricing.cta.note", "Pricing tailored to your store, set out in your Order Form.")}
          </p>
        </div>
      </section>

    </div>
  );
}
